import React from 'react';
import PropTypes from 'prop-types';
import DragSource from './dd-object-node';
import DropTarget from './dd-target-node';
import ButtonNode from './button-node';
import {DragDropContext} from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import FeedbackNode from './feedback-node';
import {filterNodeProperties} from '../../../../utilities/filter-node-properties';
import FeedbackItem from './feedback-item-node';
import {handleEvent} from '../../../../events/event-handler';


const getDataFromNode = function (node) {
    const keys = ['title', 'allow_outofbounds', 'eval_allowed_after', 'allowed_drops',
        'auto_reset', 'reset_animation_type', 'reset_easing_type',
        'reset_animation_length', 'attempts', 'enabled', 'required', 'show_correct'];
    return filterNodeProperties(node, keys, { val: node.val });
};

const getDataFromFeedbackNode = function(node) {
    const keys = ['id', 'title', 'audio', 'feedback_type'];
    return filterNodeProperties(node, keys, { val: node.val });
};

const getOriginalIdFromCopiedItem = function (id) {
    const pos = id.lastIndexOf('_dropped');
    if (pos !== -1) {
        return id.substr(0, pos);
    }
    return id;
};

const getStateFromProps = function(props = {}) {
    const droppedItemsList = props.node.childrenNamed('dd_target_node').reduce(
        (item, n) => {
            return {...item, [n.attr.id]: []};
        }, {}
    );

    const dropStateItemsOb = props.node.childrenNamed('dd_object_node').reduce(
        (item, n) => {
            return {...item, [n.attr.id]: false};
        }, {}
    );

    const numOfDraggableObjects = props.node.childrenNamed('dd_object_node').length;

    // When the number of each "dd_target_node answers" matches the number of dd_object_nodes (numOfDraggableObjects), it means that all answers are correct (MO.DYEA.3.0.0 - 523)
    const acceptAnyAnswer = (props.node.childrenNamed('dd_target_node')
        .filter(e =>  e.attr.answer_type === 'all' && (e.attr.answers ? e.attr.answers.split('|').length === numOfDraggableObjects : 0)).length === numOfDraggableObjects);

    return {
        ...getDataFromNode(props.node),
        dropStateItemsOb,
        currentFeedbackItem: null,
        overlayMask: true,
        droppedItemsList,
        ddCompleted: false,
        targetList: {},
        numOfAttempts: 0,
        acceptAnyAnswer
    };
};

export class DdNode extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            ...getStateFromProps(props)
        };

        this.enableDd = this.enableDd.bind(this);
        this.feedbackAudioEnded = this.feedbackAudioEnded.bind(this);
        this.interactionComplete = this.interactionComplete.bind(this);
        this.fetchAnswerFeedbackNode = this.fetchAnswerFeedbackNode.bind(this);
        this.checkAnswer = this.checkAnswer.bind(this);
    }


    interactionComplete() {
        setTimeout(function(){
            const event_nodes = this.props.node.childrenNamed('event_node');
            event_nodes.forEach(function (event) {
                if (event.attr.name === 'oninteractioncomplete') {
                    handleEvent(event);
                }
            });
        }.bind(this), 350);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.node != nextProps.node) {
            this.setState({
                ...getStateFromProps(nextProps)
            });
        }
    }

    componentDidMount() {
        document.addEventListener('jre-dd-enabled', this.enableDd);
        document.addEventListener('jre-audio-ended', this.enableDd);
    }

    componentWillUnmount() {
        document.removeEventListener('jre-dd-enabled', this.enableDd);
        document.removeEventListener('jre-audio-ended', this.enableDd);
    }

    enableDd() {
        const {disableJREAppPlayerContainerButtons} = this.props;
        disableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'DdNode enableDd' );
        this.setState({overlayMask: false});
    }

    /*
    Check if all the items are correctly placed inside each of their targets.
     */
    checkAnswer() {
        const { targetList = {}, droppedItemsList = {}, numOfAttempts = 0, attempts = 0, acceptAnyAnswer } = this.state;
        const {enableJREAppPlayerContainerButtons} = this.props;

        let feedbackItem = '';
        let isCorrectAnswer = true;
        let retryAllowed = true;

        for (const i in targetList) {
            const id = targetList[i].props.node.attr.id;
            let correctAnswersList = [];
            const attemptedAnswersList = droppedItemsList[id].map(node => {
                return getOriginalIdFromCopiedItem(node.id);
            });
            if (targetList[i].props.node.attr.answers) {
                correctAnswersList = targetList[i].props.node.attr.answers.split('|');
                const answerType = targetList[i].props.node.attr.answer_type;
                /* For an answer to be valid when answer type is "all", the entire list of correct answers must match
                with the answers the user provided. */
                if (answerType === 'all') {
                    const ca = new Set(correctAnswersList);
                    const aa = new Set(attemptedAnswersList);
                    // Set difference - check if the answers/array match regardless of order.
                    const difference = new Set(
                        [...ca].filter(x => !aa.has(x))
                    );
                    if (difference.size !== 0) {
                        isCorrectAnswer = false;
                        break;
                    }
                } else if(answerType === 'any') {
                    /* For an answer to be valid when answer type is "any", any one of the user's answers must match
                     * against the correct answer list */

                    if (attemptedAnswersList.length) { // if the user selected any answer
                        for (let i = 0; i < attemptedAnswersList.length; i++) {
                            const answer = attemptedAnswersList[i];
                            if (!correctAnswersList.includes(answer)) {
                                isCorrectAnswer = false;
                                break;
                            }
                        }
                    } else { // if the user didn't select any answers
                        isCorrectAnswer = false;
                    }
                } else {
                    throw new Error('Unsupported answer type.');
                }
            }
        }

        if (isCorrectAnswer || acceptAnyAnswer) {
            feedbackItem = this.fetchAnswerFeedbackNode(true);

            enableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'DdNode checkAnswer');
        } else if (numOfAttempts >= (parseInt(attempts) - 1)) {
            feedbackItem = this.fetchAnswerFeedbackNode(false);
            retryAllowed = false;

            /* We must run correctAnswers before showAnswers, because showAnswers needs to work off of a "correct" state.
             * This means that wrong answers should not be present in dd-targets when showAnswers is running.
             * Otherwise, problems will occur in pickRemainingCorrectAnswer, where it may not find a correct answer.
             */
            this.correctAnswers().then( () => {
                this.showAnswers();
                // If the button is expected to perform multiple actions, we need to go through and find any remaining
                // onclick events and trigger them. (b/c The dd-node overrides existing button_node event functionality
                // on render)
                const button_node = this.props.node.childrenNamed('button_node');
                button_node.forEach(function (button_child) {
                    const event_nodes = button_child.childrenNamed('event_node');
                    event_nodes.forEach(function (event) {
                        if (event.attr.name === 'onclick') {
                            handleEvent(event);
                        }
                    });
                });

                enableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'DdNode checkAnswer');
            });
        } else {
            // Another attempt remaining.
            feedbackItem = this.fetchAnswerFeedbackNode();
            this.correctAnswers();
        }

        this.setState({currentFeedbackItem: feedbackItem, ddCompleted: isCorrectAnswer || !retryAllowed,
            numOfAttempts: (numOfAttempts + 1)});

        return isCorrectAnswer;
    }

    /*
     * When a user has remaining answer attempts, the items they correctly dragged and dropped into the target
     * will stay there while incorrectly dropped items will reset (moving them out of their wrong target) to their
     * initial state so that the user may reattempt the answer.
     */
    correctAnswers() {
        return new Promise(resolve => { // we use a promise here to track when state has been set for "this.showAnswers()" to run
            let {dropStateItemsOb = {}, droppedItemsList = {}} = this.state;
            let newCorrectedAnswerList = {};
            const ddTargets = this.props.node.childrenNamed('dd_target_node');
            let ddAnswers = [];

            for (let targetIndex = 0; targetIndex < ddTargets.length; targetIndex++) {
                let answers = [];
                let validDroppedItemsList = [];
                if (ddTargets[targetIndex].attr.answers) {
                    answers = ddTargets[targetIndex].attr.answers.split('|');
                    validDroppedItemsList = droppedItemsList[ddTargets[targetIndex].attr.id].filter(function(item) {
                        let itemID = null;
                        if (item[0]) {
                            itemID = getOriginalIdFromCopiedItem(item[0].id);
                        } else {
                            itemID = getOriginalIdFromCopiedItem(item.id);
                        }
                        if (answers.indexOf(itemID) === -1) {
                            // Item does not belong to target - incorrect answer.
                            dropStateItemsOb[itemID] = false;
                        } else {
                            // Item belongs to target - correct answer.
                            ddAnswers.push(item);
                            return item;
                        }
                    });
                }
                else{
                    validDroppedItemsList = droppedItemsList[ddTargets[targetIndex].attr.id].filter(function(item) {
                        const itemID = getOriginalIdFromCopiedItem(item.id);
                        dropStateItemsOb[itemID] = false;
                    });
                }
                newCorrectedAnswerList[ddTargets[targetIndex].attr.id] = validDroppedItemsList;

            }

            this.setState({
                dropStateItemsOb,
                droppedItemsList: newCorrectedAnswerList
            }, () => resolve());
        });

    }

    /*
    Check if drop item is already in its target.
     */
    isDropped(id, index) {
        const { droppedItemsList = {} } = this.state;

        if (index in droppedItemsList) {
            const item = droppedItemsList[index].find(elem => elem.id === id);
            if (item) {
                return true;
            }
        }
        return false;
    }

    /*
    Find the drop target to which this drop item belongs. The excluded target ID is the "self" or the current target in
    which we can ignore for the lookup.
     */
    getObjectDropTarget(node_id, excluded_target_id) {
        const { droppedItemsList = {} } = this.state;

        // Iterate through dictionary/object.
        for (let target_id in droppedItemsList) {
            if (droppedItemsList.hasOwnProperty(target_id) && droppedItemsList[target_id] !== excluded_target_id) {
                for (let i = 0; i < droppedItemsList[target_id].length; i++) {
                    if (droppedItemsList[target_id][i].id === node_id) {
                        return target_id;
                    }
                }
            }
        }

        return null;
    }

    /*
    Handle items that are dropped-out of their target.
     */
    handleDropOut(target_id, props) {
        const id = getOriginalIdFromCopiedItem(props.id);
        const { droppedItemsList = {}, dropStateItemsOb = {}} = this.state;

        // Remove/filter-out the dropped item.
        droppedItemsList[target_id] = droppedItemsList[target_id].filter(elem => (elem.id !== id &&
            elem.id !== props.id));

        dropStateItemsOb[id] = false;
        this.setState({
            dropStateItemsOb
        });
    }

    /*
    Handle dropping item into target.
     */
    handleDrop(index, item) {
        const { dropStateItemsOb = {}, droppedItemsList = {}, targetList = {} } = this.state;
        const id = getOriginalIdFromCopiedItem(item.id);

        if (this.isDropped(id, index)) {
            return true;
        }

        const target = this.getObjectDropTarget(id, index);
        // Filter/remove items from other targets that may exist from moving from one container to another.
        if (target) {
            droppedItemsList[target] = droppedItemsList[target].filter(elem => elem.id !== id);
        }
        /* When we drop an item into a target, the item will be copied and displayed in the drop target where its ID/key
        is in the format <Original ID>_dropped ("_dropped" is appended to the original ID/key). This will remove/filter
        those copied items too */
        if (id !== item.id) {
            const target = this.getObjectDropTarget(item.id, index);
            if (target) {
                droppedItemsList[target] = droppedItemsList[target].filter(elem => elem.id !== item.id);
            }
        }

        // If the capacity on the numbers of items in the target it met, drop the first item to make space.
        if (droppedItemsList[index].length >= targetList[index].props.node.attr.capacity) {
            const firstItem = droppedItemsList[index][0];
            droppedItemsList[index].shift();
            dropStateItemsOb[getOriginalIdFromCopiedItem(firstItem.id)] = false;
            this.setState({
                dropStateItemsOb
            });
        }

        droppedItemsList[index].push(item);
        dropStateItemsOb[id] = true;
        this.setState({
            dropStateItemsOb
        });
    }

    fetchAnswerFeedbackNode(isCorrect = null) {
        const {acceptAnyAnswer} = this.state;

        let return_feedback;
        const feedback_nodes = this.props.node.childNamed('feedback_node').childrenNamed('feedback_item_node');
        if (isCorrect === true) {
            if (acceptAnyAnswer) { // if acceptAnyAnswer is true, the correct feedback is sometimes incorrect_final (MO.DYEA.3.0.0 - 523)
                return_feedback = feedback_nodes.find(n => n.attr.feedback_type === 'correct' || n.attr.feedback_type === 'incorrect_final');
            } else {
                return_feedback = feedback_nodes.find(n => n.attr.feedback_type === 'correct');
            }
        } else if (isCorrect === false) {
            return_feedback = feedback_nodes.find(n => {
                return (n.attr.feedback_type === 'incorrect_final');
            });
        } else {
            return_feedback = feedback_nodes.find(n => n.attr.feedback_type === 'incorrect_prompt');
        }

        if (return_feedback === undefined) {
            console.warn('A Feedback Node Was Not Found'); // This shouldn't ever be reached! It likely indicates an XML error.
        }
        return return_feedback;
    }

    feedbackAudioEnded() {
        if (!this.state.ddCompleted) {
            this.setState({overlayMask: false});
        }
        else {
            this.interactionComplete();
        }
    }

    feedbackAudioStart() {
        this.setState({overlayMask: true});
    }

    pickRemainingCorrectAnswer(anyTypeAnswerList, correctAnswerIdList) {
        /**
         * When showing the correct answer, pick one of the remaining unused correct answers.
         */
        const {droppedItemsList = {}} = this.state;
        const existingAnswerList = [];
        const ddTargets = this.props.node.childrenNamed('dd_target_node');
        const anyTypeDdTargetList = ddTargets.filter(x => x.attr.answer_type === 'any');
        const anyTypeDdTargetIdList = anyTypeDdTargetList.map(x => x.attr.id);

        // There are the answers that the user has dropped on target.
        for (const [dropContainerId, answerList] of Object.entries(droppedItemsList)) {
            // We're only accepting answers under the target of type "any".
            if (!anyTypeDdTargetIdList.includes(dropContainerId)) {
                continue;
            }
            for (let i = 0; i < answerList.length; i++) {
                existingAnswerList.push(answerList[i].id);
            }
        }

        const combinedAnswerIdList = existingAnswerList.concat(anyTypeAnswerList);
        // Pick the first remaining unused correct answer.
        for (let i = 0; i < correctAnswerIdList.length; i++) {
            if (!combinedAnswerIdList.includes(correctAnswerIdList[i])) {
                return correctAnswerIdList[i];
            }
        }

        return false;
    }

    showAnswers() {
        let {dropStateItemsOb = {}, droppedItemsList = {}} = this.state;
        const ddObjects = this.props.node.childrenNamed('dd_object_node');
        const ddTargets = this.props.node.childrenNamed('dd_target_node');
        const targetsAndAnswersOb = {};
        const anyTypeAnswerList = [];

        // This for loop will gather the correct answer for each target.
        for (let targetIndex = 0; targetIndex < ddTargets.length; targetIndex++) {
            const currentAnswerList = droppedItemsList[ddTargets[targetIndex].attr.id];
            const answerType = ddTargets[targetIndex].attr.answer_type;
            const updatedAnswerList = [];

            if (ddTargets[targetIndex].attr.answers) { // some ddTargets do not contain answers. skip these.
                const correctAnswerList = ddTargets[targetIndex].attr.answers.split('|');

                if (answerType === 'any') {
                    // Accept the answer if its one of the allowed answers. We're also making as assumption that
                    // "capacity" is set to 1 when answer type is "any".
                    if (currentAnswerList.length > 0 && correctAnswerList.includes(currentAnswerList[0].id)) {
                        updatedAnswerList.push({
                            id: currentAnswerList[0].node.attr.id,
                            isDropped: true,
                            node: currentAnswerList[0].node
                        });
                        anyTypeAnswerList.push(currentAnswerList[0].node.attr.id);
                    } else {
                        // If the answer is incorrect, pick one of the correct answers that has not already been used.

                        const pickedCorrectAnswerId = this.pickRemainingCorrectAnswer(anyTypeAnswerList, correctAnswerList);
                        if (!pickedCorrectAnswerId) {
                            // This exception should never occur.
                            throw Error('A correct answer was not found.');
                        }
                        const answerNode = ddObjects.find(n => n.attr.id === pickedCorrectAnswerId);
                        updatedAnswerList.push({
                            id: answerNode.attr.id,
                            isDropped: true,
                            node: answerNode
                        });
                        anyTypeAnswerList.push(answerNode.attr.id);
                    }
                } else {
                    // Get the answers for the given target.
                    for (let ansIndex = 0; ansIndex < correctAnswerList.length; ansIndex++) {
                        const answerNode = ddObjects.find(n => n.attr.id === correctAnswerList[ansIndex]);
                        if (answerNode) {
                            updatedAnswerList.push({
                                id: answerNode.attr.id,
                                isDropped: true,
                                node: answerNode
                            });
                        }
                    }
                }
            }

            targetsAndAnswersOb[ddTargets[targetIndex].attr.id] = updatedAnswerList;
        }

        const globalAnswerIdList = [];
        for (const [, answerList] of Object.entries(targetsAndAnswersOb)) {
            for (let i = 0; i < answerList.length; i++) {
                globalAnswerIdList.push(answerList[i].id);
            }
        }

        // Determine which items should remain un-dropped - those are the items which have not been
        // taken as answers and therefore are not inside one of the the drop targets.
        dropStateItemsOb = Object.keys(dropStateItemsOb).reduce((obj, answerId) => (
            {...obj, [answerId]: globalAnswerIdList.includes(answerId)}
        ), {});

        this.setState({
            dropStateItemsOb,
            droppedItemsList: targetsAndAnswersOb,
            overlayMask: true, // prevent user from clicking 'Done' quickly after answers are set. Without this, feedback will play as correct.
        });
    }

    render() {
        const {overlayMask = true, droppedItemsList = {}, currentFeedbackItem = null, dropStateItemsOb = {},
            targetList = {}} = this.state;
        const {moduleURL = ''} = this.props;

        return (
            <div>
                {overlayMask ? <div className="overlay-mask" /> : ''}
                <div className="node jre-dd-node">
                    {this.props.node.children.map((n, ) => {
                        switch (n.name) {
                            case 'dd_target_node':
                                return <DropTarget
                                    moduleURL={moduleURL}
                                    droppedItems={droppedItemsList}
                                    ref={item => targetList[n.attr.id] = item}
                                    onDropOut={this.handleDropOut.bind(this, n.attr.id)}
                                    onDrop={item => this.handleDrop(n.attr.id, item)} id={n.attr.id}
                                    key={n.attr.id}
                                    node={n}
                                />;
                            case 'button_node':
                                return <ButtonNode
                                    moduleURL={moduleURL}
                                    onClick={this.checkAnswer}
                                    key={n.attr.id}
                                    node={n}
                                />;
                            case 'dd_object_node':
                                return <DragSource
                                    moduleURL={moduleURL}
                                    id={n.attr.id}
                                    isDropped={dropStateItemsOb[n.attr.id]}
                                    key={n.attr.id}
                                    node={n}
                                />;
                            case 'feedback_node':
                                return <FeedbackNode
                                    moduleURL={moduleURL}
                                    id={n.attr.id}
                                    key={n.attr.id}
                                    node={n}
                                />;
                            default:
                                return null;
                        }
                    })}
                </div>
                {
                    currentFeedbackItem &&
                    <div className="node feedback-item">
                        <FeedbackItem
                            moduleURL={moduleURL}
                            node={getDataFromFeedbackNode(currentFeedbackItem)}
                            onAudioEnded={this.feedbackAudioEnded.bind(this)}
                            onAudioStart={this.feedbackAudioStart.bind(this)}
                            key={currentFeedbackItem.attr.id} />
                    </div>
                }
            </div>
        );
    }
}

DdNode.propTypes = {
    node: PropTypes.object.isRequired
};

export default DragDropContext(HTML5Backend)(DdNode);



// WEBPACK FOOTER //
// ./src/js/components/app/jre/elements/dd-node.js